import { useMemo } from 'react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import type { ManualAccountEditProps } from '@/components/ManualAccountEdit/ManualAccountEdit.types'
import { ManualAccountForm } from '@/components/ManualAccountForm/ManualAccountForm'
import type { ManualAccountFormSchema } from '@/components/ManualAccountForm/ManualAccountForm.types'
import { manualAccountFormDefaultValues } from '@/components/ManualAccountForm/ManualAccountForm.values'

import { INVALIDATE_KEYS } from '@/constants/invalidateAccountQueryKeys.constants'

import {
  useBankingServiceBankingControllerEditManualAccount,
  useBankingServiceBankingControllerGetManualAccount,
} from '@/__generated__/queries'

export function ManualAccountEdit({
  accountId,
  onClose,
}: ManualAccountEditProps) {
  const { data, isFetching, isLoading } =
    useBankingServiceBankingControllerGetManualAccount({
      accountId: accountId.replace('manual-', ''),
    })

  const initialValues = useMemo<ManualAccountFormSchema | undefined>(() => {
    if (!data) {
      return undefined
    }

    return manualAccountFormDefaultValues(data)
  }, [data])

  const queryClient = useQueryClient()

  const updateManualAccountMutation =
    useBankingServiceBankingControllerEditManualAccount({
      onSuccess: () => {
        notifications.show({
          color: 'green',
          message: 'Account updated successfully',
          title: 'Account updated',
        })

        INVALIDATE_KEYS.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: [key],
          })
        })

        onClose()
      },
    })

  const handleSubmit = (values: ManualAccountFormSchema) =>
    updateManualAccountMutation.mutate({
      accountId: accountId.replace('manual-', ''),
      requestBody: values,
    })

  return (
    <ManualAccountForm
      data={initialValues}
      edit
      isLoading={isLoading || isFetching}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  )
}
